import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Tag } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConfigFormShell, jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';

interface CustomHeader { name: string; value: string }
interface Form {
  x_content_type_options: string;
  x_frame_options: string;
  x_xss_protection: string;
  referrer_policy: string;
  content_security_policy: string;
  permissions_policy: string;
  custom_headers: CustomHeader[];
}
const DEFAULTS: Form = {
  x_content_type_options: '',
  x_frame_options: '',
  x_xss_protection: '',
  referrer_policy: '',
  content_security_policy: '',
  permissions_policy: '',
  custom_headers: [],
};
const REFERRER = ['', 'no-referrer', 'no-referrer-when-downgrade', 'origin', 'origin-when-cross-origin', 'same-origin', 'strict-origin', 'strict-origin-when-cross-origin', 'unsafe-url'];
const FRAME = ['', 'DENY', 'SAMEORIGIN'];
const NOSNIFF = ['', 'nosniff'];
const XSS = ['', '0', '1'];
const DISABLED = 'Disabled';

function hydrate(cfg: Record<string, unknown>): Form {
  return {
    x_content_type_options: (cfg.x_content_type_options as string) || '',
    x_frame_options: (cfg.x_frame_options as string) || '',
    x_xss_protection: (cfg.x_xss_protection as string) || '',
    referrer_policy: (cfg.referrer_policy as string) || '',
    content_security_policy: (cfg.content_security_policy as string) || '',
    permissions_policy: (cfg.permissions_policy as string) || '',
    custom_headers: Array.isArray(cfg.custom_headers) ? (cfg.custom_headers as CustomHeader[]) : [],
  };
}

export function SecurityHeadersTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'security-headers', { vendorMarkers: ['referrer_policy', 'x_frame_options', 'custom_headers'] });
  const isConfigured = !!cfg;

  const [form, setForm] = useState<Form>(DEFAULTS);
  const [snap, setSnap] = useState<Form>(DEFAULTS);
  useEffect(() => {
    const next = cfg ? hydrate(cfg) : DEFAULTS;
    setForm(next);
    setSnap(next);
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual(form, snap), [form, snap]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const update = useUpdateCdnConfig(domainId, 'security-headers', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'security_headers', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Security headers saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save', description: errorMessage(e, 'Could not save') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'security-headers', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'security_headers', 'delete');
      show({ tone: 'success', title: 'Security headers removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove', description: errorMessage(e, 'Could not remove') }),
  });
  const busy = update.isPending || remove.isPending;

  const sel = (label: string, key: keyof Form, opts: string[]) => (
    <Field label={label}>
      {(p) => (
        <Select value={(form[key] as string) || DISABLED} onValueChange={(v) => set(key, (v === DISABLED ? '' : v) as Form[typeof key])}>
          <SelectTrigger {...p}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={o || DISABLED} value={o || DISABLED}>
                {o || DISABLED}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  );

  return (
    <ConfigFormShell
      title="Security Headers"
      description={`Standard security response headers for ${domain}.`}
      isLoading={isLoading}
      isConfigured={isConfigured}
      dirty={dirty}
      busy={busy}
      saving={update.isPending}
      onSave={() => update.mutate(form as unknown as Record<string, unknown>)}
      onRemove={() => remove.mutate()}
      drift={<CdnAspectDrift domainId={domainId} path="security-headers" />}
      onCancel={() => setForm(snap)}
    >
      <Card>
        <CardHeader>
          <CardTitle>Standard headers</CardTitle>
          <CardDescription>Common protections. “Disabled” omits the header.</CardDescription>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            {sel('X-Content-Type-Options', 'x_content_type_options', NOSNIFF)}
            {sel('X-Frame-Options', 'x_frame_options', FRAME)}
            {sel('X-XSS-Protection', 'x_xss_protection', XSS)}
            {sel('Referrer-Policy', 'referrer_policy', REFERRER)}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Policies</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Field label="Content-Security-Policy">
            {(p) => <Textarea {...p} rows={3} value={form.content_security_policy} onChange={(e) => set('content_security_policy', e.target.value)} style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }} />}
          </Field>
          <Field label="Permissions-Policy">
            {(p) => <Textarea {...p} rows={2} value={form.permissions_policy} onChange={(e) => set('permissions_policy', e.target.value)} style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }} />}
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => set('custom_headers', [...form.custom_headers, { name: '', value: '' }])}>
              Add header
            </Button>
          }
        >
          <CardTitle>Custom headers</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {form.custom_headers.length === 0 ? (
            <EmptyState size="sm" icon={<Tag size={20} />} title="No custom headers yet" description="Click Add header to inject a response header." />
          ) : (
            form.custom_headers.map((h, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                <Input value={h.name} placeholder="Header name" style={{ flex: 1 }} onChange={(e) => set('custom_headers', form.custom_headers.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))} />
                <Input value={h.value} placeholder="Header value" style={{ flex: 2 }} onChange={(e) => set('custom_headers', form.custom_headers.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)))} />
                <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => set('custom_headers', form.custom_headers.filter((_, i) => i !== idx))}>
                  <Trash2 size={15} />
                </IconButton>
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </ConfigFormShell>
  );
}
